import {
  type Action,
  type CryptoCurrencyDataSource,
  type FiatCurrency,
  setActionPrices,
} from '@0xtorch/core'
import {
  FailedToAnalyzeEvmChainException,
  FailedToAnalyzeEvmTxException,
  FailedToFetchEvmIndexDataException,
  FailedToFetchEvmTxsException,
  RateLimitExceededException,
  divideArrayIntoChunks,
} from '@pkg/basic'
import type { AccountEvm } from '@pkg/database-portfolio'
import {
  deleteAccounts,
  getEvmTxIndexes,
  ignoreLockedActionEvmTxs,
  saveEvmTxAndActions,
  selectEvmAccountsWithoutActions,
} from '@pkg/database-portfolio'
import type { AssetFetchClient } from '@pkg/datasource-client'
import {
  type Chain,
  type Converter,
  type Hex,
  type LowerHex,
  type TransactionDecoded,
  countTokenTransferLogs,
  isNoApiExplorer,
} from '@pkg/evm'
import { createEvmDatasourceCache } from '../createEvmDatasourceCache'
import { normalizeChainActionEvidences } from '../normalizeChainActionEvidences'
import type {
  AnalyzeEvmTransactionParameters,
  FetchEvmTransactionsParameters,
  FetchEvmTransactionsReturnTypes,
  RunToDatabase,
} from '../types'
import {
  fetchAndSaveAddressData,
  fetchAndSaveAddressesData,
} from './fetchAndSaveAddressData'
import { fetchTransactionCounts } from './fetchTransactionCounts'
import { saveTransactionLog } from './saveTransactionLog'

const transferFunctionInterfaces = new Set([
  'safeTransferFrom(address, address, uint256)',
  'transfer(address, uint256)',
])

const analyzingEvmChains = new Set<number>()

type AnalyzeEvmAccountsParameters = {
  readFromDatabase: RunToDatabase
  writeToDatabase: RunToDatabase
  getToken?: () => Promise<string | null>
  startTime: number
  chain: Chain
  accounts: readonly AccountEvm[]
  allEvmAccountAddressList: readonly LowerHex[]
  fiatCurrency: FiatCurrency
  cryptoCurrencyDataSource: CryptoCurrencyDataSource
  assetFetchClient: AssetFetchClient
  apiEndpoint: string
  datasourceApiEndpoint: string
  evmTokenApiEndpoint: string
  fetchTransactions: (
    parameters: FetchEvmTransactionsParameters,
  ) => Promise<FetchEvmTransactionsReturnTypes>
  analyzeTransaction: (parameters: AnalyzeEvmTransactionParameters) => Promise<{
    readonly transaction: TransactionDecoded
    readonly actions: readonly Action[]
  }>
  converter: Converter
  onStart: (accountIdSet: Set<number>) => void
  onEnd: () => void
  onFetchedAccount: (accountId: number) => void
  onStartAnalyzeTransaction: (transactionHashSet: Set<Hex>) => void
  onFetchedTransaction: (transactionHash: Hex) => void
  onAnalyzedTransaction: (transactionHash: Hex) => void
  onError: (error: unknown) => void
}

export const analyzeEvmAccounts = async ({
  readFromDatabase,
  writeToDatabase,
  getToken,
  startTime,
  chain,
  accounts,
  allEvmAccountAddressList,
  fiatCurrency,
  cryptoCurrencyDataSource,
  assetFetchClient,
  apiEndpoint,
  datasourceApiEndpoint,
  evmTokenApiEndpoint,
  fetchTransactions,
  analyzeTransaction,
  converter,
  onStart,
  onEnd,
  onFetchedAccount,
  onStartAnalyzeTransaction,
  onFetchedTransaction,
  onAnalyzedTransaction,
  onError,
}: AnalyzeEvmAccountsParameters): Promise<void> => {
  if (analyzingEvmChains.has(chain.id)) {
    return
  }
  analyzingEvmChains.add(chain.id)
  onStart(new Set(accounts.map(({ id }) => id)))

  try {
    // action がない evm account id set 取得・作成
    const accountsWithoutActions = await readFromDatabase((database) =>
      selectEvmAccountsWithoutActions({ database }),
    )
    const withoutActionAccountIds = new Set(
      accountsWithoutActions.map(({ id }) => id),
    )

    // 最終更新から 1 時間以上経過してる account を昇順ソート
    const oneHourAgo = Date.now() - 1000 * 60 * 60
    const unactiveAccountIds: number[] = []
    const oldAccounts: AccountEvm[] = []
    for (const account of accounts) {
      if (withoutActionAccountIds.has(account.id) && account.lastSyncedAt) {
        // Check if account has any activity
        const [transactionCount, nativeBalance, code] = await Promise.all([
          chain.client.call({
            clientCall: (client) =>
              client.getTransactionCount({ address: account.evmAddress }),
          }),
          chain.client.call({
            clientCall: (client) =>
              client.getBalance({ address: account.evmAddress }),
          }),
          chain.client.call({
            clientCall: (client) =>
              client.getCode({ address: account.evmAddress }),
          }),
        ])
        const hasActivity =
          transactionCount > 0 ||
          nativeBalance > 0n ||
          (code !== undefined && BigInt(code) > 0n)
        if (!hasActivity && !account.syncing) {
          unactiveAccountIds.push(account.id)
          onFetchedAccount(account.id)
          continue
        }
      }

      // Skip if synced within 1 hour, unless evmToBlock is null (full resync requested)
      if (
        account.evmToBlock !== undefined &&
        account.lastSyncedAt !== undefined &&
        account.lastSyncedAt >= oneHourAgo
      ) {
        // account data 取得完了 callback
        onFetchedAccount(account.id)
        continue
      }
      oldAccounts.push(account)
    }
    const sortedAccounts = oldAccounts.sort(
      (a, b) => (a.lastSyncedAt ?? 0) - (b.lastSyncedAt ?? 0),
    )

    // unactiveAccountIds を DB から削除
    if (unactiveAccountIds.length > 0) {
      await writeToDatabase((database) =>
        deleteAccounts({
          database,
          accountIds: unactiveAccountIds,
        }),
      )
    }

    // explorer API がない chain は index data を取得できないため skip する
    if (isNoApiExplorer({ explorer: chain.explorer })) {
      for (const account of sortedAccounts) {
        onFetchedAccount(account.id)
      }
    } else {
      // 最新 block number を取得
      const toBlock = await chain.client.call({
        clientCall: (client) => client.getBlockNumber(),
      })

      if (chain.explorer.getAddressesHistoryPage !== undefined) {
        const transactionCounts = await fetchTransactionCounts({
          chain,
          addrsses: sortedAccounts.map(({ evmAddress }) => evmAddress),
        })
        await fetchAndSaveAddressesData({
          accounts: sortedAccounts.map((account, index) => ({
            account,
            transactionCount: transactionCounts[index],
          })),
          chain,
          toBlock: Number(toBlock),
          startTime,
          writeToDatabase,
        })
        for (const account of sortedAccounts) onFetchedAccount(account.id)
      } else {
        // account を 10 件毎に分割して処理
        const accountChunks = divideArrayIntoChunks(sortedAccounts, 10)
        for (const targetAccounts of accountChunks) {
          // transaction count を取得
          const transactionCounts = await fetchTransactionCounts({
            chain,
            addrsses: targetAccounts.map(({ evmAddress }) => evmAddress),
          })

          // Account 毎の index & internal transaction data を取得
          for (const [index, account] of targetAccounts.entries()) {
            const transactionCount = transactionCounts[index]
            await fetchAndSaveAddressData({
              writeToDatabase,
              startTime,
              chain,
              account,
              getToken,
              toBlock: Number(toBlock),
              transactionCount,
            }).catch((error) => {
              if (
                error instanceof Error &&
                error.message.includes('Status: 401') &&
                chain.id === 2020
              ) {
                throw new RateLimitExceededException(error, chain.name)
              }
              console.warn('Failed to fetch EVM index data', {
                error,
                chainId: chain.id,
                chainName: chain.name,
                address: account.evmAddress,
              })
              onError(
                new FailedToFetchEvmIndexDataException(
                  error,
                  chain.name,
                  account.evmAddress,
                ),
              )
              return undefined
            })
            // account data 取得完了 callback
            onFetchedAccount(account.id)
          }
        }
      }
    }
  } catch (error) {
    if (error instanceof RateLimitExceededException) {
      onError(error)
    } else {
      onError(new FailedToAnalyzeEvmChainException(error, chain.name))
    }
  }

  // tx 取得失敗は通知が大量にならないよう、チェーン単位で集約して最後に 1 回だけ通知する
  const fetchTxErrors = new Map<LowerHex, unknown>()
  try {
    // analyzed = false な tx index data 及び関連 internal transaction data を DB から取得
    const { indexes, internalTransactions } = await readFromDatabase(
      (database) =>
        getEvmTxIndexes({
          database,
          chainId: chain.id,
          analyzed: false,
          startTime,
        }),
    )

    // indexes.length が 0 の場合は終了
    if (indexes.length === 0) {
      return
    }

    // 該当 tx を source とする isLocked=true の action が存在する tx は除外する
    // 除外した tx について、 accountEvmIndexRelation.analyzed を true に更新
    const ignoreHashes = await writeToDatabase((database) =>
      ignoreLockedActionEvmTxs({
        database,
        chainId: chain.id,
        hashes: indexes.map(({ hash }) => hash),
      }),
    )
    const targetIndexes = indexes.filter(({ hash }) => !ignoreHashes.has(hash))

    // targetIndexes が空の場合は終了
    if (targetIndexes.length === 0) {
      return
    }

    // transaction 解析開始 callback
    onStartAnalyzeTransaction(new Set(targetIndexes.map(({ hash }) => hash)))

    // filter をかけた index data を 100件毎に分割して100件毎処理
    const chunkSize = 100
    for (const indexChunk of divideArrayIntoChunks(targetIndexes, chunkSize)) {
      // transaction detail 一括取得
      const { transactions, errors } = await fetchTransactions({
        chain,
        indexes: indexChunk,
        internalTransactionList: internalTransactions,
        onFetched: onFetchedTransaction,
      })
      for (const [hash, error] of errors.entries()) {
        console.warn('Failed to fetch EVM transaction data', {
          error,
          chainId: chain.id,
          chainName: chain.name,
          hash,
        })
        fetchTxErrors.set(hash, error)
      }

      // datasource cache 作成
      await createEvmDatasourceCache({
        assetFetchClient,
        datasourceApiEndpoint,
        evmTokenApiEndpoint,
        chainId: chain.id,
        transactionList: transactions,
        accountAddresses: new Set(allEvmAccountAddressList),
      })

      const decodedTxs: TransactionDecoded[] = []
      const tokenTransferCounts = new Map<LowerHex, number>()
      const actions: Action[] = []

      // tx 1件ずつ解析
      for (const transaction of transactions) {
        try {
          const { transaction: decodedTx, actions: generatedActions } =
            await analyzeTransaction({
              chain,
              transaction,
              assetFetchClient,
              datasourceApiEndpoint,
              evmTokenApiEndpoint,
              accountAddressList: allEvmAccountAddressList,
              converter,
            })
          const normalizedActions = normalizeChainActionEvidences({
            actions: generatedActions,
          })
          decodedTxs.push(decodedTx)
          tokenTransferCounts.set(
            decodedTx.hash,
            countTokenTransferLogs({ receipt: decodedTx }),
          )
          actions.push(...normalizedActions)

          // evidence=none の function call tx は log 保存
          if (
            getToken !== undefined &&
            decodedTx.input.length >= 10 &&
            normalizedActions.some(({ evidence }) => evidence === 'none') &&
            !transferFunctionInterfaces.has(decodedTx.function?.interface ?? '')
          ) {
            void saveTransactionLog({
              endpoint: apiEndpoint,
              getToken,
              transaction: decodedTx,
            }).catch((error) => {
              console.warn('Supplementary transaction log upload failed', error)
            })
          }
        } catch (error) {
          onError(
            new FailedToAnalyzeEvmTxException(
              error,
              chain.name,
              transaction.hash,
            ),
          )
        }
        // transaction 解析完了 callback
        onAnalyzedTransaction(transaction.hash)
      }

      // 生成した action の価格設定
      const pricedActions = await setActionPrices({
        actions,
        fiat: fiatCurrency,
        dataSource: cryptoCurrencyDataSource,
      })

      // - rule に合わせて action 更新
      // - DB に action 保存 & 関連データ更新
      // portfolio.isTxGenerated を false に更新
      // accountEvmIndexRelation.analyzed を true に更新
      // decoded transaction を actionSource に保存
      // action を保存・ rule があれば rule に合わせて更新
      await writeToDatabase((database) =>
        saveEvmTxAndActions({
          database,
          chainId: chain.id,
          transactions: decodedTxs,
          actions: pricedActions,
          tokenTransferCounts,
        }),
      )
    }
  } catch (error) {
    onError(new FailedToAnalyzeEvmChainException(error, chain.name))
  } finally {
    if (fetchTxErrors.size > 0) {
      onError(
        new FailedToFetchEvmTxsException({
          chainName: chain.name,
          errors: fetchTxErrors,
        }),
      )
    }

    // 処理完了時に解析中から削除する
    analyzingEvmChains.delete(chain.id)

    // 処理完了を callback
    onEnd()
  }
}
